import React, { useState } from 'react';

// components
import TeamMemberSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TeamMemberSelection';
import TransactionAmountSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/TransactionAmountSelection';
import AddTechnicianName from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/technical/AddTechnicianName';
import AddNotesForMachineTransaction from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/AddNotesForMachineTransaction';
import DeviceTransactionFinalReviewScreen from 'components/admin/deviceDashboard/deviceEditor/simulator/transaction/DeviceTransactionFinalReviewScreen';

// Utils
import { convertDollarsToCents } from 'utils/convertMoney';

const TechnicalServiceDeviceTransactionSimulator = (props) => {
  const { device, onBack, onSubmit, teamMembers } = props;
  const technicalServiceDeviceTransactionScreens = {
    USER_SELECTION: 'USER_SELECTION',
    TECHNICIAN_SELECTION: 'TECHNICIAN_SELECTION',
    ADD_NOTES: 'ADD_NOTES',
    TRANSACTION_AMOUNT: 'TRANSACTION_AMOUNT',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };

  // form data
  const [wizardStep, setWizardStep] = useState(
    technicalServiceDeviceTransactionScreens.USER_SELECTION,
  );
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTeamMemberDisplay, setSelectedTeamMemberDisplay] = useState(null);
  const [selectedTechnicianName, setSelectedTechnicianName] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState(null);
  const [selectedTransactionAmountInDollars, setSelectedTransactionAmountInDollars] =
    useState(null);

  /**
   * send formatted data from the wizard steps to the parent to complete
   */
  const submitTechnicalServiceSimulationData = () => {
    const dataToSubmit = {
      deviceId: device.id,
      machineId: device.machineId,
      transactionAmount: convertDollarsToCents(selectedTransactionAmountInDollars),
      userId: selectedUserId,
      technicianName: selectedTechnicianName,
      note: selectedNotes,
    };

    return onSubmit(dataToSubmit);
  }

  /**
   * navigate backwards in the wizard based on current progress in the wizard
   */
  const handleOnBackFunctionality = () => {
    switch (wizardStep) {
      case technicalServiceDeviceTransactionScreens.USER_SELECTION:
        return onBack();
      case technicalServiceDeviceTransactionScreens.TECHNICIAN_SELECTION:
        setSelectedUserId(null);
        setSelectedTeamMemberDisplay(null);
        return setWizardStep(technicalServiceDeviceTransactionScreens.USER_SELECTION);
      case technicalServiceDeviceTransactionScreens.ADD_NOTES:
        setSelectedTechnicianName(null);
        return setWizardStep(technicalServiceDeviceTransactionScreens.TECHNICIAN_SELECTION);
      case technicalServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        setSelectedNotes(null);
        return setWizardStep(technicalServiceDeviceTransactionScreens.ADD_NOTES);
      case technicalServiceDeviceTransactionScreens.FINAL_REVIEW:
        setSelectedTransactionAmountInDollars(null);
        return setWizardStep(technicalServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
      default:
        return onBack();
    }
  };

  /**
   * determine the logic for disabling the final submit button
   */
  const disableFinalSubmitButton = () => {
    return !selectedUserId || !selectedTechnicianName || !selectedTransactionAmountInDollars;
  };

  /**
   * render the final review screen prior to submitting the simulation event
   */
  const renderFinalReviewScreen = () => {
    return (
      <DeviceTransactionFinalReviewScreen
        deviceId={device.id}
        machineId={device.machineId}
        selectedTransactionAmount={selectedTransactionAmountInDollars}
        selectedTechnicianName={selectedTechnicianName}
        selectedTeamMemberDisplay={selectedTeamMemberDisplay}
        selectedNotes={selectedNotes}
        onBack={handleOnBackFunctionality}
        onNext={submitTechnicalServiceSimulationData}
        disableFinalSubmitButton={disableFinalSubmitButton}
      />
    )
  }

  /**
   * navigate forwards to the final review screen once transaction amount is identified
   */
  const handleOnNextForTransactionAmountSelection = () => {
    setWizardStep(technicalServiceDeviceTransactionScreens.FINAL_REVIEW);
  };

  /**
   * render the transaction amount input
   */
  const renderTransactionAmountSelection = () => {
    return (
      <TransactionAmountSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForTransactionAmountSelection}
        selectedTransactionAmountInDollars={selectedTransactionAmountInDollars}
        setSelectedTransactionAmount={(amount) => {
          setSelectedTransactionAmountInDollars(Number(amount));
        }}
      />
    );
  };

  /**
   * Navigate the user forward to the transaction amount screen
   */
  const handleOnNextForNotesSection = () => {
    setWizardStep(technicalServiceDeviceTransactionScreens.TRANSACTION_AMOUNT);
  }
  
  /**
   * render the area where notes related to the transaction can be added
   */
  const renderNotesSection = () => {
    return (
      <AddNotesForMachineTransaction
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForNotesSection}
        selectedNotes={selectedNotes}
        setSelectedNotes={setSelectedNotes}
      />
    );
  }

  /**
   * Navigate forward in the wizard to the notes form
   */
  const handleOnNextForTechnicianNameSelection = () => {
    setWizardStep(technicalServiceDeviceTransactionScreens.ADD_NOTES);
  }

  /**
   * Render the screen that allows you to enter the technician name
   */
  const renderTechnicianNameSelection = () => {
    return (
      <AddTechnicianName
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForTechnicianNameSelection}
        selectedTechnicianName={selectedTechnicianName}
        setSelectedTechnicianName={setSelectedTechnicianName}
      />
    );
  }

  /**
   * Determine which series of steps to show based on the origination point selection
   */
  const handleOnNextForUserSelection = () => {
    setWizardStep(technicalServiceDeviceTransactionScreens.TECHNICIAN_SELECTION);
  };

  /**
   * set the user ID and display name selections for the team member assigned to the turn
   * 
   * @param {object} userData 
   */
  const setValuesForUserSelection = (userData) => {
    setSelectedUserId(userData.userId);
    setSelectedTeamMemberDisplay(`${userData.firstName} ${userData.lastName}`);
  }

  /**
   * render the self-service transaction origination point selection
   */
  const renderTeamMemberSelection = () => {
    return (
      <TeamMemberSelection
        onBack={handleOnBackFunctionality}
        onNext={handleOnNextForUserSelection}
        selectedTeamMemberDisplay={selectedTeamMemberDisplay}
        selectedUserId={selectedUserId}
        onValueChange={setValuesForUserSelection}
        teamMembers={teamMembers}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderTechnicalServiceTransactionWizardSteps = () => {
    switch (wizardStep) {
      case technicalServiceDeviceTransactionScreens.USER_SELECTION:
        return renderTeamMemberSelection();
      case technicalServiceDeviceTransactionScreens.TECHNICIAN_SELECTION:
        return renderTechnicianNameSelection();
      case technicalServiceDeviceTransactionScreens.ADD_NOTES:
        return renderNotesSection();
      case technicalServiceDeviceTransactionScreens.TRANSACTION_AMOUNT:
        return renderTransactionAmountSelection();
        case technicalServiceDeviceTransactionScreens.FINAL_REVIEW:
          return renderFinalReviewScreen();
      default:
        return renderTeamMemberSelection();
    }
  };

  return <>{renderTechnicalServiceTransactionWizardSteps()}</>;
};

export default TechnicalServiceDeviceTransactionSimulator;
